import { NavigationProp, ParamListBase, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  GetAdminArticlesParams,
  GetAdminEventsParams,
  GetAdminExtremeEventsParams,
  GetAdminHelpdeskCategoriesParams,
  GetAdminHelpdeskIssuesParams,
  HelpdeskCategories,
  HelpdeskIssueStatus,
  ModuleKey,
} from '../client/interfaces';
import { ErrorType } from '../resources/interfaces';

export type NavigationParamList = {
  [ScreenName.HomeScreen]: undefined;
  [ScreenName.FavoritesScreen]: undefined;
  [ScreenName.PlatformsScreen]: undefined;
  [ScreenName.SettingsScreen]: undefined;
  [ScreenName.ArticlesScreen]: { isBookmarked?: boolean };
  [ScreenName.EventsScreen]: { isBookmarked?: boolean };
  [ScreenName.HelpdeskScreen]: undefined;
  [ScreenName.ProfileScreen]: undefined;
  [ScreenName.ArticleScreen]: { postId: number };
  [ScreenName.EventScreen]: { postId: number };
  [ScreenName.PostFilterScreen]: { fromScreen: ScreenName };
  [ScreenName.OnboardingScreen]: undefined;
  [ScreenName.PrivacyPolicyScreen]: undefined;
  [ScreenName.AdminIndexScreen]: undefined;
  [ScreenName.AdminArticleListScreen]: GetAdminArticlesParams;
  [ScreenName.AdminArticleEditScreen]: { postId?: number };
  [ScreenName.AdminExtremeEventListScreen]: GetAdminExtremeEventsParams;
  [ScreenName.AdminEventListScreen]: GetAdminEventsParams;
  [ScreenName.AdminExtremeEventEditScreen]: { postId?: number };
  [ScreenName.AdminEventEditScreen]: { postId?: number };
  [ScreenName.AdminHelpdeskCategoriesScreen]: GetAdminHelpdeskCategoriesParams & {
    activeTab?: HelpdeskCategories;
  };
  [ScreenName.AdminHelpdeskTopicEditScreen]: {
    topicId?: number;
    returnScreen?: ScreenName;
    returnPostId?: number;
  };
  [ScreenName.AdminHelpdeskOrganizationEditScreen]: {
    organizationId?: number;
    returnScreen?: ScreenName;
    returnPostId?: number;
  };
  [ScreenName.AdminHelpdeskIssueListScreen]: GetAdminHelpdeskIssuesParams;
  [ScreenName.AdminHelpdeskIssueEditScreen]: {
    postId?: number;
    initialStatus?: HelpdeskIssueStatus;
  };
  [StackName.ErrorStack]: { type?: ErrorType };
  [ScreenName.PersonalizationScreen]: undefined;
  [ScreenName.NotificationsScreen]: { fromScreen: ScreenName };
  [ScreenName.SplashScreen]: undefined;
  [ScreenName.BirthdayScreen]: { notificationId: number };
  '*': { type?: ErrorType };
};

export type AppNavigationProp = Omit<NavigationProp<NavigationParamList>, 'navigate'> & {
  navigate<T extends keyof NavigationParamList>(screen: T, params?: NavigationParamList[T]): void;
};

export enum Navigator {
  main = 'main',
  profile = 'profile',
  admin = 'admin',
}

export interface Screen {
  name: ScreenName;
  stackName?: StackName;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: any;
  translation: string;
  navigator?: Navigator;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
  moduleKey?: ModuleKey; // Ki-be kapcsolható backenden
}

export enum ScreenName {
  HomeScreen = 'home',
  EventsScreen = 'events',
  PlatformsScreen = 'platforms',
  ArticlesScreen = 'articles',
  HelpdeskScreen = 'helpdesk',
  FavoritesScreen = 'favorites',
  SettingsScreen = 'settings',
  AdminIndexScreen = 'admin',
  AdminArticleListScreen = 'adminArticles',
  AdminArticleEditScreen = 'adminArticleEdit',
  AdminExtremeEventListScreen = 'adminExtremeEvents',
  AdminExtremeEventEditScreen = 'adminExtremeEventEdit',
  AdminEventListScreen = 'adminEvents',
  AdminEventEditScreen = 'adminEventEdit',
  AdminHelpdeskCategoriesScreen = 'adminHelpdeskCategories',
  AdminHelpdeskTopicEditScreen = 'adminHelpdeskTopicEdit',
  AdminHelpdeskOrganizationEditScreen = 'adminHelpdeskOrganizationEdit',
  AdminHelpdeskIssueListScreen = 'adminHelpdeskIssues',
  AdminHelpdeskIssueEditScreen = 'adminHelpdeskIssueEdit',
  ProfileScreen = 'profile',
  OnboardingScreen = 'onboarding',
  PrivacyPolicyScreen = 'privacyPolicy',
  ArticleScreen = 'article',
  EventScreen = 'event',
  ErrorScreen = 'error',
  PostFilterScreen = 'filter',
  NotFound = '*',
  PersonalizationScreen = 'personalization',
  NotificationsScreen = 'notifications',
  SplashScreen = 'splash',
  BirthdayScreen = 'birthday',
}

export enum StackName {
  HomeStack = 'HomeStack',
  ArticlesStack = 'ArticlesStack',
  EventsStack = 'EventsStack',
  PlatformsStack = 'PlatformsStack',
  HelpdeskStack = 'HelpdeskStack',
  AdminStack = 'AdminStack',
  SettingsStack = 'SettingsStack',
  FavoritesStack = 'FavoritesStack',
  OnboardingStack = 'OnboardingStack',
  ErrorStack = 'ErrorStack',
  NotificationsStack = 'NotificationsStack',
  ProfileStack = 'ProfileStack',
  BirthdayStack = 'BirthdayStack',
}

export type NotificationRedirectScreenName = Extract<
  ScreenName,
  | ScreenName.ArticleScreen
  | ScreenName.EventScreen
  | ScreenName.HomeScreen
  | ScreenName.BirthdayScreen
>;

export type TabNavigationScreenProps<
  ParamList extends ParamListBase,
  RouteName extends keyof ParamList = keyof ParamList,
  NavigatorID extends string | undefined = undefined,
> = {
  navigation: NavigationProp<ParamList, RouteName, NavigatorID>;
  route: RouteProp<ParamList, RouteName>;
};

export type StackNavigationScreenProps<
  ParamList extends Record<string, object | undefined>,
  RouteName extends keyof ParamList = keyof ParamList,
> = {
  navigation: StackNavigationProp<ParamList, RouteName>;
  route: RouteProp<ParamList, RouteName>;
};

export type TabNavigationOptions = {
  title?: string;
};

export type TabNavigationEventMap = {
  tabPress: {
    data: { isAlreadyFocused: boolean };
    canPreventDefault: true;
  };
};
